import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

const WhyBuyDirect = () => {
  const [ref, isInView] = useInView({ threshold: 0.2 });

  const benefits = [
    {
      icon: 'ri-price-tag-3-line',
      title: 'Eliminate Distributor Markup',
      description: 'Skip the middleman and save 20-40% by purchasing directly from verified global manufacturers. No hidden fees or unnecessary margins.',
      savings: 'Save 20-40%',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Factory-Direct Pricing',
      description: 'Access wholesale pricing typically reserved for large-scale buyers. Volume discounts and manufacturer-direct rates on every order.',
      savings: 'Wholesale Rates',
    },
    {
      icon: 'ri-eye-line',
      title: 'Transparent Sourcing',
      description: 'Full visibility into your supply chain. Know exactly where materials come from, manufacturing standards, and quality certifications.',
      savings: 'Complete Visibility',
    },
    {
      icon: 'ri-stack-line',
      title: 'Bulk Savings',
      description: 'Leverage our purchasing power across thousands of projects. Consolidated shipping and bulk order discounts reduce per-unit costs.',
      savings: 'Volume Discounts',
    },
    {
      icon: 'ri-tools-line',
      title: 'Custom Manufacturing',
      description: 'Work directly with factories to customize materials to your exact specifications. No compromises on design or functionality.',
      savings: 'Tailored Solutions',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Control Process',
      description: 'Multi-stage inspections from factory floor to final delivery. Third-party verification, compliance testing, and documentation.',
      savings: 'Guaranteed Quality',
    },
  ];

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="text-xs font-semibold text-secondary-600 tracking-widest uppercase mb-4">
            THE GO GLOBAL ADVANTAGE
          </div>
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl text-gray-900 mb-6">
            Why Buy <span className="font-semibold">Direct?</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Traditional distribution adds 30-50% to material costs. We eliminate unnecessary markups and deliver factory-direct savings to your bottom line.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-xl hover:border-secondary-300 transition-all duration-300 group"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <i className={`${benefit.icon} text-white text-3xl`}></i>
                </div>
                <span className="text-xs font-bold text-secondary-600 bg-secondary-50 px-3 py-1 rounded-full whitespace-nowrap">
                  {benefit.savings}
                </span>
              </div>
              <h3 className="font-display font-semibold text-xl text-gray-900 mb-3">
                {benefit.title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-10 text-center text-white"
        >
          <h3 className="font-display font-semibold text-2xl md:text-3xl mb-4">
            Ready to Cut Your Material Costs?
          </h3>
          <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
            Get a free cost comparison and see exactly how much you can save on your next project.
          </p>
          <a
            href="#quote-form"
            className="inline-flex items-center px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Get Free Cost Comparison
            <i className="ri-arrow-right-line ml-2"></i>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyBuyDirect;
